import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../../Translation";
import Badge from "../../Badge";
import { ArrowDownIcon, ArrowsPointingOutIcon, ChevronDownIcon, Cog8ToothIcon, DocumentIcon, ExclamationCircleIcon, MinusIcon, PlusIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { iteration, split, webhook } from "../../../Resources/Images";
import { TextInput } from "../../Inputs/TextInput";
import { TextArea } from "../../Inputs/TextArea";
import { Button } from "../../Buttons";
import Tabs, { useTabs } from "../../Tabs";
import { useAPI } from "../../../API";
import Select from "../../Inputs/Select";
import { ClockIcon } from "@heroicons/react/20/solid";
import { bgColor, checkedBg, checkedBorder, classNames, focusOutline, hoverBgColor, indeterminateBg, indeterminateBorder } from "../../../Resources/CSS/styling";
import Checkbox from "../../Inputs/Checkbox";
import { TagIcon, VariableIcon } from "@heroicons/react/24/solid";
import Radio from "../../Inputs/Radio";
import { textColor } from "../../../Resources/CSS/styling";

const getSourceOptions = ({translate}) => {
    return [
        { id: 'all_contacts', name: translate('automatisation.from_all_contacts') },
        { id: 'tag', name: translate('automatisation.tag') },
        { id: 'contact', name: translate('automatisation.from_contact') },
        { id: 'event', name: translate('automatisation.from_event') },
    ]
}

const ConfigurationTab = ({ tmpStep, setTmpStep, save, error, isCreate, tags = [] }) => {

    const { translate } = useTranslation();
    const api = useAPI();
   

    const setTag = (tag) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: tag.id } });
    }

    const selectedTag = tags.length > 0 ? tags.find((tag) => tag.id === parseInt(tmpStep.data.value)) || tags[0] : null;

    const sourceOptions = getSourceOptions({translate});

    const selectedSource = sourceOptions.find((option) => option.id === tmpStep.data.source) || sourceOptions[0];

    const variableTypes = [
        { id: 1, name: translate('automatisation.string'), value: 'string' },
        { id: 2, name: translate('automatisation.number'), value: 'number' },
        { id: 3, name: translate('automatisation.boolean'), value: 'boolean' },
        { id: 4, name: translate('automatisation.string_variable'), value: 'string_variable' },
        { id: 5, name: translate('automatisation.number_variable'), value: 'number_variable' },
        { id: 6, name: translate('automatisation.boolean_variable'), value: 'boolean_variable' }
    ]

    const getOperationOptions = (variableType) => {
        const operations = [
            { id: 1, name: translate('automatisation.is_equal'), value: 'EQUALS' },
            { id: 2, name: translate('automatisation.is_not_equal'), value: 'NOT_EQUALS' },
            ...(variableType === 'number' || variableType === 'number_variable' ? [{ id: 3, name: translate('automatisation.is_greater'), value: 'GREATER_THAN' },{ id: 4, name: translate('automatisation.is_less'), value: 'LESS_THAN' }] : []),
            ...(variableType === 'string' || variableType === 'string_variable' ? [{ id: 5, name: translate('automatisation.contains'), value: 'CONTAINS' }, { id: 6, name: translate('automatisation.does_not_contain'), value: 'NOT_CONTAINS' }] : []),
            ...(variableType === 'number_variable' || variableType === 'string_variable' || variableType === 'boolean_variable' ? [] : [{ id: 7, name: translate('automatisation.is_null_or_empty'), value: 'IS_NULL_OR_EMPTY' }, { id: 8, name: translate('automatisation.is_not_null_or_empty'), value: 'IS_NOT_NULL_OR_EMPTY' }]),
        ];
        return operations;
    };

    const variableSourceOptions = [
        { id: 'event', name: translate('automatisation.from_event') },
        { id: 'contact', name: translate('automatisation.from_contact') },
        { id: 'global', name: translate('automatisation.from_global') }
    ];

    const setSource = (source) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, source: source.id, value: null } });
    }

    const setValue = (value) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: value } });
    }

    const setDelayBetweenIterations = (delayBetweenIterations) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, delay_between_iterations: delayBetweenIterations } });
    }

    const addContactFilter = () => {
        const newMapping = tmpStep.data.contact_filters ? [...tmpStep.data.contact_filters, { variable: '', variable_type: 'STRING', operation: 'EQUALS', value: '' }] : [{ variable: '', variable_type: 'STRING', operation: 'EQUALS', value: '' }];
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, contact_filters: newMapping } });
    }
  
    const removeContactFilter = (index) => {
        const newMapping = tmpStep.data.contact_filters.filter((_, i) => i !== index);
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, contact_filters: newMapping } });
    }

    const updateContactFilter = (index, key, value) => {
        const newMapping = [...tmpStep.data.contact_filters];
        newMapping[index][key] = value;
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, contact_filters: newMapping } });
    }

    const getVariableType = (variableType) => {
        return variableTypes.find((type) => type.value.toLowerCase() === variableType.toLowerCase()) || variableTypes[0];
    }

    const getOperation = (operation, variableType) => {
        return getOperationOptions(getVariableType(variableType).value).find((op) => op.value.toLowerCase() === operation.toLowerCase()) || getOperationOptions(getVariableType(variableType))[0];
    }

    const getVariableSource = (variableSourceOptions, variableValue) => {
        return variableSourceOptions.find(source => variableValue?.startsWith && variableValue?.startsWith(source.id)) || variableSourceOptions[0];
    }

    const setValueSource = (source, index) => {
        const contactFilter = tmpStep.data.contact_filters[index];
        const newVariableValueWithoutSource = contactFilter.value.split(".").slice(1).join(".");
        updateContactFilter(index, 'value', source.id + newVariableValueWithoutSource);
    }

    const updateValueWithVariableSource = (index, key, value) => {
        const contactFilter = tmpStep.data.contact_filters[index];
        const source = getVariableSource(variableSourceOptions, contactFilter.value);
        updateContactFilter(index, key, source.id + "." + value);
    }

    return (
        <div className="flex flex-col mt-4 overflow-visible">

            {error && <p className="text-red-600 flex flex-row items-center bg-red-50 p-4"><ExclamationCircleIcon className="w-6 h-6 mr-2" />{error}</p>}

            <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.source')}</p>
            <Select selected={selectedSource} options={sourceOptions} setSelected={(e) => setSource(e)} />

            {selectedSource.id === 'tag' && <div className="flex flex-col mt-2">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.tag')}</p>
                {tags && <Select selected={selectedTag} options={tags} setSelected={(e) => setTag(e)} />}   
            </div>}

            {(selectedSource.id === 'contact' || selectedSource.id === 'event') && <div className="flex flex-col mt-2">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.variable')}</p>
                <TextInput value={tmpStep.data.value} onChange={(e) => setValue(e.target.value)} />
            </div>}

            <div className="flex flex-col mt-4">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.delay_between_iterations')}</p>
                <TextInput type="number" value={tmpStep.data.delay_between_iterations} onChange={(e) => setDelayBetweenIterations(e.target.value)} />
            </div>

            {(selectedSource.id === 'tag' || selectedSource.id === 'all_contacts') && <div className="flex flex-col mt-4">
                <div className="flex flex-row items-center justify-between">
                    <p className="text-xl text-gray-600 font-bold">{translate('automatisation.contact_filters')}</p>
                    <PlusIcon onClick={addContactFilter} className="w-6 h-6 text-gray-600 cursor-pointer hover:text-gray-300" />
                </div>

                <div className="flex flex-col w-full flex-1 gap-4 mt-2">
                {(tmpStep.data.contact_filters || []).map((contactFilter, index) => (
                    <div className="flex flex-col justify-between border border-gray-400 rounded-md p-2">
                        <div className="flex flex-row items-center justify-between">
                            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.variable')}</p>
                            <MinusIcon onClick={() => removeContactFilter(index)} className="w-6 h-6 text-red-600 cursor-pointer ml-2 hover:text-red-300" />
                        </div>
                        <div className="flex flex-1 w-full flex-col">
                            <TextInput value={contactFilter.variable} onChange={(e) => updateContactFilter(index, 'variable', e.target.value)} />
                        </div>
                        <div className="flex flex-1 w-full flex-col mt-2">
                            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.operation')}</p>
                            <Select options={getOperationOptions(getVariableType(contactFilter.variable_type).value)} selected={getOperation(contactFilter.operation, contactFilter.variable_type)} setSelected={(e) => updateContactFilter(index, 'operation', e.value)} />
                        </div>
                        <div className="flex flex-1 w-full flex-col mt-2">
                            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.value_type')}</p>
                            <Select options={variableTypes} selected={getVariableType(contactFilter.variable_type)} setSelected={(e) => updateContactFilter(index, 'variable_type', e.value)} />
                        </div>

                        {(getVariableType(contactFilter.variable_type).value === 'string_variable' || getVariableType(contactFilter.variable_type).value === 'number_variable' || getVariableType(contactFilter.variable_type).value === 'boolean_variable') && <div className="flex flex-1 w-full flex-col mt-2">
                            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.from')}</p>
                            <Select options={variableSourceOptions} selected={getVariableSource(variableSourceOptions, contactFilter.value)} setSelected={(e) => setValueSource(e, index)} />
                            
                            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.value')}</p>
                            <TextInput value={contactFilter.value.split(".").slice(1).join(".")} onChange={(e) => updateValueWithVariableSource(index, 'value', e.target.value)} />
                        </div>}
                        
                        {!getVariableType(contactFilter.variable_type).value?.includes('variable') && !(getOperation(contactFilter.operation, contactFilter.variable_type).value == 'IS_NULL_OR_EMPTY' || getOperation(contactFilter.operation, contactFilter.variable_type).value == 'IS_NOT_NULL_OR_EMPTY') && <div className="flex flex-1 w-full flex-col">
                            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.value')}</p>
                            <TextInput value={contactFilter.value} onChange={(e) => updateContactFilter(index, 'value', e.target.value)} />
                        </div>}
                    </div>
                ))}
                </div>
            </div>}

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

const DescriptionTab = ({ tmpStep, setTmpStep, save, isCreate }) => {
    const { translate } = useTranslation();
    return (
        <div className="flex flex-col mt-4">
            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.description')}</p>
            <TextArea rows={5} value={tmpStep.description} onChange={(e) => setTmpStep({ ...tmpStep, description: e.target.value })} />

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep, JSON.stringify(tmpStep.data.body))}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

export default function LoopPanel({ panelHook, step, refresh, deleteStep, isCreate, onCreateNewStep }) {
    const { translate } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [tmpStep, setTmpStep] = useState({ ...step });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const api = useAPI();

    const tabs = useTabs([
        { icon: Cog8ToothIcon, name: translate('automatisation.configuration'), current: true },
        { icon: DocumentIcon, name: translate('automatisation.description'), current: false }
    ]);
    
    // Reset tmpStep when panel is closed
    useEffect(() => {
        if (!panelHook.open) {
            setTmpStep({ ...step });
        }
    }, [panelHook.open]);

    const [tags, setTags] = useState([]);

    useEffect(() => {
        api.listTags(1,99999).then((result) => setTags(result.data.tags));
    }, []);

    const save = async (tmpStep) => {


        try {
            if (isCreate) {

                if (!tmpStep.description || tmpStep.description.trim() === '') {

                    const sourceOptions = getSourceOptions({translate});

                    const selectedSource = sourceOptions.find((option) => option.id === tmpStep.data.source) || sourceOptions[0];

                    if (selectedSource.id === 'tag') {
                        const tag = tags.find((tag) => tag.id === parseInt(tmpStep.data.value));
                        tmpStep.description = "Loop through contacts in tag " + (tag ? tag.name : tmpStep.data.value);
                    } else if (selectedSource.id === 'contact') {
                        tmpStep.description = "Loop through variable in contact";
                    } else if (selectedSource.id === 'event') {
                        tmpStep.description = "Loop through variable in event";
                    } else {
                        tmpStep.description = "Loop through all contacts";
                    }
                }

                const newStepResponse = await api.createAutomatisationStep(tmpStep.automatisation_id, tmpStep);
                if (newStepResponse.data.automatisation_step) {
                    onCreateNewStep(newStepResponse.data.automatisation_step);
                }
            } else {
                await api.updateAutomatisationStep(tmpStep.automatisation_id, tmpStep);
            }

            panelHook.setOpen(false);
            setTimeout(() => {
                refresh();
            }, 1000);
        } catch (e) {
            setError('Invalid JSON');
            return;
        }

    }


    return (
        <div className="flex flex-col flex-1 rounded-md p-4 overflow-hidden h-full w-96">
            <div className="flex flex-row items-center">
                <img src={iteration} alt="iteration" className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md mr-2 ${textColor}`} />
                <div className="flex flex-col ml-2">
                    <h2 className="text-xl font-bold">{translate('automatisation.loop')}</h2>
                </div>
                
                <div className="flex flex-row flex-1 justify-end">

                {!isCreate && <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={deleteStep}>
                        {translate('delete')}
                    </Button>}
                {isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => save(tmpStep)}>{translate('create')}</Button>}
                </div>
            </div>

            <div className="flex flex-row overflow-x-scroll no-scrollbar">
                <Tabs tabs={tabs} />
            </div>

            {tabs.currentTabIndex === 0 && <ConfigurationTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} tags={tags} />}
            {tabs.currentTabIndex === 1 && <DescriptionTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
        </div>
    )
}